import { Button, Callout, InputGroup, Intent } from '@blueprintjs/core'
import * as React from 'react'
import { useState } from 'react'
import * as Sentry from '@sentry/browser'
import { RouteComponentProps } from 'react-router'
import { IconNames } from '@blueprintjs/icons'
import Client from '../../client'
import { showFailure, showToast } from '../../Toast'
import { getErrorString } from '../../utils/serverErrorUtils'
import PrinterV2 from '../../printing/printer'
import { ProgressBar } from './ProgressBar'
import { Timer } from './Timer'
import { MAX_PRINTABLE_SKU_LENGTH } from './expressListPrint'
import { createEmptyExpressListProduct } from './utils'

type Props = RouteComponentProps

interface PrintFailureState {
  productId: number
  skuTooLong?: boolean
}

const style = {
  flexCentered: { display: 'flex', justifyContent: 'center', gap: 10 },
  wideEnough: { width: 300 }
}

const getPrintFailureFromRouterState = (state: unknown): PrintFailureState | null => {
  if (!state || typeof state !== 'object') return null

  const { printFailed, printProductId, printSkuTooLong } = state as Record<string, unknown>
  if (printFailed && printProductId) {
    return {
      productId: printProductId as number,
      skuTooLong: printSkuTooLong === true
    }
  }

  return null
}

export const ExpressListStart: React.FC<Props> = props => {
  const [search, setSearch] = useState('')

  const [loading, setLoading] = useState(false)
  const [printFailure, setPrintFailure] = useState<PrintFailureState | null>(() =>
    getPrintFailureFromRouterState(props.history.location.state)
  )
  const [retryingPrint, setRetryingPrint] = useState(false)

  const startNewProduct = () => {
    props.history.push('/express_list/new', createEmptyExpressListProduct())
  }

  const submit = async event => {
    event.preventDefault()

    setLoading(true)
    try {
      // draft true, because non-draft products have already been listed.
      const res = await Client.v5ProductsIndex({ query: search, draft: true, perPage: 1 })
      if (res.data?.products?.[0]?.title) {
        const product = res.data.products[0]

        // pass the API body into the next step via react-router,
        // so that we don't have to make another API call to initialize the form
        props.history.push(`/express_list/${product.id}`, product)
      } else {
        showToast('Product not found', Intent.WARNING)
      }
    } catch (e) {
      showFailure(getErrorString(e, Sentry.captureException))
    } finally {
      setLoading(false)
    }
  }

  const retryPrint = async () => {
    if (!printFailure || printFailure.skuTooLong) return

    setRetryingPrint(true)
    try {
      const printer = new PrinterV2()
      const printed = await printer.printProductLabelFromId([printFailure.productId])
      if (printed) {
        setPrintFailure(null)
        showToast('Label printed', Intent.SUCCESS)
      }
    } catch {
      // Keep the warning visible so the user can retry again.
    } finally {
      setRetryingPrint(false)
    }
  }

  const reactRouterState: any = props.history.location.state

  // We want to display the final time after saving until the start of the next listing.
  // The real ProgressBar is on ExpressListForm, under an entirely different route.
  // So we display a fake ProgressBar here with a frozen Timer, but only if they just completed a listing.
  const maybeHideProgressBar: Record<string, unknown> = {}
  const justCompletedAListing =
    reactRouterState && reactRouterState.requiredFieldCount && reactRouterState.elapsedSeconds
  if (!justCompletedAListing) {
    maybeHideProgressBar.visibility = 'hidden'
  }

  return (
    <div className="express-list-start-page">
      <div className="header sticky">
        <div className="header-content">
          <div className="left-header-things">
            {/* Placeholder button to make h1 placement similar between start page and express list form */}
            <Button text="Back" icon={IconNames.CHEVRON_LEFT} style={{ visibility: 'hidden' }} />
            <h1>Express List</h1>

            <ProgressBar
              formLoading={false}
              requiredFieldCount={reactRouterState?.requiredFieldCount}
              missingRequiredFields={[]}
              topRightElement={<Timer elapsedSeconds={reactRouterState?.elapsedSeconds} />}
              style={maybeHideProgressBar}
            />
          </div>
        </div>
        <div className="dummy-collapsed-drawer-spacer" />
      </div>

      {printFailure && (
        <div className="callout-container">
          <div className="callout-content">
            <Callout intent={Intent.WARNING}>
              Product saved and listed, but printing failed.{' '}
              {printFailure.skuTooLong ? (
                <>Shorten to {MAX_PRINTABLE_SKU_LENGTH} characters or less to print.</>
              ) : (
                <Button small intent={Intent.WARNING} loading={retryingPrint} onClick={retryPrint}>
                  Retry print
                </Button>
              )}
            </Callout>
          </div>
        </div>
      )}

      <form onSubmit={submit} style={style.flexCentered}>
        <InputGroup
          value={search}
          onChange={v => setSearch(v.currentTarget.value)}
          placeholder="Search product by SKU, ID, or title"
          leftIcon="search"
          autoFocus
          style={style.wideEnough}
        />
        <Button type="submit" loading={loading} disabled={search.length === 0} intent={Intent.PRIMARY}>
          Go
        </Button>
      </form>

      <div style={style.flexCentered}>
        <p> Scan or enter SKU, ID, or title to start the next Express List for a single-item product</p>
      </div>

      <div className="start-new-product-container">
        <p>OR</p>
        <Button type="button" intent={Intent.PRIMARY} onClick={startNewProduct}>
          Start New Product
        </Button>
      </div>
    </div>
  )
}
